<template>
  <div class="presentation-clock flex items-center space-x-3">
    <!-- Current Time Display -->
    <div class="bg-card/80 backdrop-blur-sm border rounded-lg px-3 py-1 text-sm font-medium">
      <div class="flex items-center space-x-2">
        <Clock class="w-4 h-4" />
        <span>{{ currentTime }}</span>
      </div>
    </div>

    <!-- Auto-advance Controls -->
    <div v-if="showSlideControls" class="flex items-center space-x-2">
      <!-- Auto-advance Toggle -->
      <Button
        variant="outline"
        size="sm"
        @click="slidesStore.toggleAutoAdvance()"
        :class="[
          slidesStore.isAutoAdvanceEnabled 
            ? 'bg-primary text-primary-foreground' 
            : ''
        ]"
        :title="slidesStore.isAutoAdvanceEnabled ? 'Stop Auto-advance' : 'Start Auto-advance'"
      >
        <Play v-if="!slidesStore.isAutoAdvanceEnabled" class="w-4 h-4 mr-1" />
        <Pause v-else class="w-4 h-4 mr-1" />
        {{ slidesStore.isAutoAdvanceEnabled ? 'Auto' : 'Manual' }}
      </Button>

      <!-- Timer Display with Progress -->
      <div 
        v-if="slidesStore.isAutoAdvanceEnabled" 
        class="bg-card/80 backdrop-blur-sm border rounded-lg px-3 py-1 text-sm font-medium relative overflow-hidden"
      >
        <!-- Progress Bar Background -->
        <div 
          class="absolute inset-0 bg-primary/20 transition-all duration-1000 ease-linear"
          :style="{ width: `${slideProgress}%` }"
        ></div>
        
        <!-- Timer Text -->
        <div class="relative flex items-center space-x-2">
          <Timer class="w-4 h-4" />
          <span>{{ remainingTime }}s</span>
        </div>
      </div>

      <!-- Duration Settings -->
      <div class="flex items-center space-x-1">
        <Button
          variant="ghost"
          size="sm"
          @click="adjustDuration(-5)"
          :disabled="currentDuration <= 5"
          title="Decrease duration by 5s"
          class="px-2"
        >
          <Minus class="w-3 h-3" />
        </Button>
        
        <div class="bg-card/80 backdrop-blur-sm border rounded px-2 py-1 text-xs font-medium min-w-[3rem] text-center">
          {{ currentDuration }}s
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          @click="adjustDuration(5)"
          :disabled="currentDuration >= 60"
          title="Increase duration by 5s"
          class="px-2"
        >
          <Plus class="w-3 h-3" />
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSlidesStore } from '~/stores/slides'
import { Button } from '@/components/ui/button'
import { Clock, Play, Pause, Timer, Plus, Minus } from 'lucide-vue-next'

const route = useRoute()
const slidesStore = useSlidesStore()

// Current time display
const currentTime = ref('')
const timeInterval = ref<NodeJS.Timeout>()

// Timer update interval
const timerInterval = ref<NodeJS.Timeout>()
const remainingTime = ref(0)
const slideProgress = ref(0)

// Show slide controls only on the main pitch deck page
const showSlideControls = computed(() => {
  return route.path === '/'
})

// Get current slide duration
const currentDuration = computed(() => {
  return slidesStore.currentSlide?.duration || slidesStore.defaultSlideDuration
})

// Update current time
const updateCurrentTime = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

// Update timer display
const updateTimer = () => {
  if (slidesStore.isAutoAdvanceEnabled) {
    remainingTime.value = slidesStore.getRemainingTime()
    slideProgress.value = slidesStore.getSlideProgress()
  }
}

// Adjust slide duration
const adjustDuration = (delta: number) => {
  const newDuration = Math.max(5, Math.min(60, currentDuration.value + delta))
  
  if (slidesStore.currentSlide?.duration !== undefined) {
    // Update current slide's custom duration
    slidesStore.updateSlideDuration(slidesStore.currentSlide.id, newDuration)
  } else {
    // Update default duration
    slidesStore.setDefaultDuration(newDuration)
  }
}

onMounted(() => {
  // Update current time every second
  updateCurrentTime()
  timeInterval.value = setInterval(updateCurrentTime, 1000)
  
  // Update timer display more frequently for smooth progress bar
  updateTimer()
  timerInterval.value = setInterval(updateTimer, 100)
})

onUnmounted(() => {
  if (timeInterval.value) {
    clearInterval(timeInterval.value)
  }
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
  }
})
</script>

<style scoped>
.presentation-clock {
  /* Ensure the component doesn't interfere with other layout */
  flex-shrink: 0;
}

/* Smooth transitions for the progress bar */
.bg-primary\/20 {
  transition: width 0.1s ease-out;
}
</style>
